import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAbility } from '@casl/react';
import { ArrowLeft, Loader2, Lock, Warehouse } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import AccessRestricted from '../../components/AccessRestricted';
import { AbilityContext } from '../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import { Branch, BranchContact } from '../../types/lists.types';
import { StagedMember, YardDetail, YardPayload } from '../../types/stock.types';
import { branchesApi } from '../../utils/lists-api';
import { yardsApi } from '../../utils/stock-api';
import api from '../../utils/api';
import YardMembersSection from './components/YardMembersSection';

const LIST_PATH = '/stock/settings/yards';
/** Lets the Save button live below the Members section and still submit the details form. */
const FORM_ID = 'yard-details-form';

const EMPTY: YardPayload = {
    yardName: '',
    yardCode: '',
    branchId: null,
    address: '',
    description: '',
};

/**
 * Create New Yard / Manage Yard — STOCK Settings (STOCK.md §9).
 *
 * A full page rather than a dialog, matching every other create/edit screen in Idarah:
 * the record gets its own URL, so it can be linked, reloaded and backed out of.
 *
 * One component serves both modes. They differ only in whether an id is present — the
 * fields, validation and error handling are identical, and forking them would mean
 * maintaining the same eight rules twice.
 *
 * Both modes show Details AND Members, because a yard's managers ARE its members
 * (§5.4) — there is no manager field. On create the members are staged here and sent
 * with the yard; on edit they write straight through.
 */
export default function YardFormPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const ability = useAbility(AbilityContext);

    const isEdit = Boolean(id);
    const yardId = id ? Number(id) : null;
    const canSave = ability.can(ACTIONS.MANAGE, RESOURCES.STOCK_ADMIN);

    const [form, setForm] = useState<YardPayload>(EMPTY);
    /** Create only — on edit the Members section owns its own rows. */
    const [staged, setStaged] = useState<StagedMember[]>([]);
    const [yard, setYard] = useState<YardDetail | null>(null);
    const [branches, setBranches] = useState<Branch[]>([]);
    const [users, setUsers] = useState<BranchContact[]>([]);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    /**
     * Whether the code is frozen. Only the server knows — it depends on order numbers
     * already allocated — so it is read from the record rather than guessed.
     */
    const codeLocked = Boolean(yard?.yardCodeLocked);

    // Selector options: Active branches only, but every user (§2.1).
    useEffect(() => {
        if (!canSave) return;
        branchesApi.list({ status: 'active' }).then(setBranches).catch(() => undefined);
        api.get<{ success: boolean; users: BranchContact[] }>('/api/users?all_users=true')
            .then((res) => setUsers(Array.isArray(res?.users) ? res.users : []))
            .catch(() => toast.error('Failed to load people list'));
    }, [canSave]);

    const loadYard = useCallback(async () => {
        if (!yardId) return;
        try {
            setLoading(true);
            const detail = await yardsApi.get(yardId);
            setYard(detail);
            setForm({
                yardName: detail.yardName,
                yardCode: detail.yardCode,
                branchId: detail.branch?.id ?? null,
                address: detail.address,
                description: detail.description ?? '',
            });
        } catch (err: any) {
            toast.error(err?.message || 'Failed to load yard');
            navigate(LIST_PATH);
        } finally {
            setLoading(false);
        }
    }, [yardId, navigate]);

    useEffect(() => {
        if (canSave && isEdit) loadYard();
    }, [canSave, isEdit, loadYard]);

    const validate = () => {
        const next: Record<string, string> = {};
        if (!form.yardName.trim()) next.yardName = 'Yard Name is required';
        if (!form.yardCode.trim()) next.yardCode = 'Yard Code is required';
        else if (!/^[A-Z0-9]+$/i.test(form.yardCode.trim())) {
            next.yardCode = 'Yard Code can only contain letters and numbers';
        }
        if (!form.branchId) next.branchId = 'Branch is required';
        if (!form.address.trim()) next.address = 'Address is required';
        // Create only: on edit the Members section enforces this itself, by refusing to
        // remove the last manager. The server checks it either way.
        if (!isEdit && !staged.some((m) => m.yardRole === 'yard_manager')) {
            next.members = 'A yard needs at least one Yard Manager.';
        }
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;

        const payload: YardPayload = {
            ...form,
            yardCode: form.yardCode.trim().toUpperCase(),
            description: form.description?.trim() || null,
        };

        try {
            setSaving(true);
            if (yardId) {
                // Never resend a frozen code — the server rejects a change outright.
                const { yardCode, ...rest } = payload;
                await yardsApi.update(yardId, codeLocked ? rest : payload);
                toast.success('Yard updated');
            } else {
                // Yard and members commit together, or neither does.
                await yardsApi.create({ ...payload, members: staged });
                toast.success('Yard created');
            }
            // The sidebar renders yard nodes from this list, so it has to hear about it.
            window.dispatchEvent(new CustomEvent('stock-yards-changed'));
            navigate(LIST_PATH);
        } catch (err: any) {
            // The server names the offending field on conflicts (duplicate code/name,
            // locked code) — surface it inline rather than only as a toast.
            if (err?.field) setErrors({ [err.field]: err.message });
            toast.error(err?.message || 'Failed to save yard');
        } finally {
            setSaving(false);
        }
    };

    // No margin of its own — each field wrapper is space-y-2, which spaces this too.
    const err = (field: string) =>
        errors[field] ? <p className="text-xs text-destructive">{errors[field]}</p> : null;

    if (!canSave) return <AccessRestricted />;

    const backButton = (
        <Button variant="ghost" size="sm" onClick={() => navigate(LIST_PATH)} className="mb-2 -ml-2">
            <ArrowLeft size={16} className="mr-1.5" />Yards
        </Button>
    );

    if (loading) {
        return (
            <div className="space-y-4 md:space-y-6 p-2 md:p-0 max-w-3xl">
                {backButton}
                <div className="space-y-3 animate-pulse">
                    <div className="h-8 w-56 bg-gray-300 rounded-md" />
                    {[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-14 bg-gray-200 rounded-md" />)}
                </div>
            </div>
        );
    }

    // A closed yard is an archive (§10). Editing it would rewrite settled history.
    if (yard?.status === 'closed') {
        return (
            <div className="space-y-4 md:space-y-6 p-2 md:p-0 max-w-3xl">
                {backButton}
                <div>
                    <h1 className="text-2xl font-bold">{yard.yardName}</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        This yard is closed and read-only. Reopen it from the Closed Yards tab
                        before making changes.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4 md:space-y-6 p-2 md:p-0 max-w-3xl">
            {/* Header */}
            <div>
                {backButton}
                <h1 className="text-2xl font-bold">{isEdit ? 'Manage Yard' : 'Create New Yard'}</h1>
                <p className="text-sm text-muted-foreground mt-1">
                    {isEdit
                        ? `Details and membership for ${yard?.yardName ?? 'this yard'}.`
                        : 'Yards are the physical stock-holding locations that orders belong to.'}
                </p>
            </div>

            <form id={FORM_ID} onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
                <Card>
                    <CardContent className="p-4 md:p-6 space-y-5">
                        <div>
                            <h2 className="text-base font-semibold flex items-center gap-2">
                                <Warehouse size={18} className="text-muted-foreground" />Details
                            </h2>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="yardName">Yard Name *</Label>
                            <Input
                                id="yardName"
                                value={form.yardName}
                                onChange={(e) => setForm({ ...form, yardName: e.target.value })}
                                placeholder="Sydney Main Yard"
                            />
                            {err('yardName')}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="yardCode" className="flex items-center gap-1.5">
                                Yard Code *
                                {codeLocked && <Lock size={12} className="text-muted-foreground" />}
                            </Label>
                            <Input
                                id="yardCode"
                                value={form.yardCode}
                                disabled={codeLocked}
                                onChange={(e) =>
                                    setForm({ ...form, yardCode: e.target.value.toUpperCase() })}
                                placeholder="SYD1"
                                maxLength={10}
                                className="uppercase"
                            />
                            <p className="text-xs text-muted-foreground">
                                {codeLocked
                                    ? 'Locked — this code is already embedded in existing order numbers.'
                                    : 'Used in every order number for this yard, e.g. RET-SYD1-00042. Choose carefully: it cannot be changed once an order is raised.'}
                            </p>
                            {err('yardCode')}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="branchId">Branch *</Label>
                            <Select
                                value={form.branchId ? String(form.branchId) : undefined}
                                onValueChange={(v) => setForm({ ...form, branchId: Number(v) })}
                            >
                                <SelectTrigger id="branchId">
                                    <SelectValue placeholder={
                                        branches.length ? 'Select a branch' : 'No active branches available'
                                    } />
                                </SelectTrigger>
                                <SelectContent>
                                    {branches.map((b) => (
                                        <SelectItem key={b.id} value={String(b.id)}>{b.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {branches.length === 0 && (
                                <p className="text-xs text-muted-foreground">
                                    A yard must belong to a branch. Create one under Lists → Branches first.
                                </p>
                            )}
                            {err('branchId')}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="address">Address *</Label>
                            <Input
                                id="address"
                                value={form.address}
                                onChange={(e) => setForm({ ...form, address: e.target.value })}
                                placeholder="12 Industrial Way, Wetherill Park NSW"
                            />
                            {err('address')}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="description">Description</Label>
                            <Textarea
                                id="description"
                                value={form.description ?? ''}
                                onChange={(e) => setForm({ ...form, description: e.target.value })}
                                rows={3}
                            />
                        </div>
                    </CardContent>
                </Card>
            </form>

            {/*
             * Members lives outside the <form> deliberately: nesting its buttons inside
             * would make them submit the details form. On create it stages rows that go
             * with the yard; on edit it writes straight through.
             */}
            <YardMembersSection
                yard={yard}
                users={users}
                staged={staged}
                onStagedChange={setStaged}
                error={errors.members}
            />

            {/*
             * Below both sections, so Create Yard sits under everything it will save.
             * `form=` is what lets a button outside the <form> still submit it.
             */}
            <div className="flex flex-col-reverse md:flex-row md:justify-end gap-2">
                <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate(LIST_PATH)}
                    className="w-full md:w-auto"
                >
                    Cancel
                </Button>
                <Button
                    type="submit"
                    form={FORM_ID}
                    disabled={saving}
                    className="w-full md:w-auto bg-slate-900 hover:bg-slate-800 text-white border-0"
                >
                    {saving && <Loader2 size={16} className="animate-spin mr-2" />}
                    {isEdit ? 'Save Changes' : 'Create Yard'}
                </Button>
            </div>
        </div>
    );
}
